import type { components as internalComponents } from '@/generated/internal-api/openapi-types';
import type { components as messagingComponents } from '@/generated/messaging-api/openapi-types';

export type AgentType = internalComponents['schemas']['AgentType.schema'];
export type AgentSessionApiId = internalComponents['schemas']['AgentSessionApiId.schema'];
export type AgentSessionStatus = internalComponents['schemas']['AgentSessionStatus.schema'];
export type AgentDefinitionV1 = internalComponents['schemas']['AgentDefinitionV1.schema'];
export type AgentSessionSummaryV1 = internalComponents['schemas']['AgentSessionSummaryV1.schema'];
export type AgentSessionV1 = internalComponents['schemas']['AgentSessionV1.schema'];
export type AgentFindingCountsV1 = internalComponents['schemas']['AgentFindingCountsV1.schema'];
export type StudyHealthCheckSessionSummaryV1 = internalComponents['schemas']['StudyHealthCheckSessionSummaryV1.schema'];
export type ProtocolFormat = internalComponents['schemas']['ProtocolFormat.schema'];
export type SendValidatorDomainConfirmationV1 =
  internalComponents['schemas']['SendValidatorDomainConfirmationV1.schema'];

/**
 * A report node's scorecard. Generated from the schema rather than hand-written: unlike the SEND
 * result types below, this payload has a real OpenAPI contract, so the types come from the generator
 * and cannot drift from the server.
 */
export type AgentReportV1 = internalComponents['schemas']['AgentReportV1.schema'];
export type ScorecardFindingV1 = AgentReportV1['findings'][number];
export type ScorecardSeverity = ScorecardFindingV1['severity'];

export type AgentTreeV1 = internalComponents['schemas']['AgentTreeV1.schema'];
export type AgentTreeNodeV1 = internalComponents['schemas']['AgentTreeNodeV1.schema'];
export type AgentTreeSessionV1 = internalComponents['schemas']['AgentTreeSessionV1.schema'];
export type AgentInputV1 = internalComponents['schemas']['AgentInputV1.schema'];
export type AgentTreeNodeApiId = internalComponents['schemas']['AgentTreeNodeApiId.schema'];
export type AgentTreeNodeType = internalComponents['schemas']['AgentTreeNodeType.schema'];

/** A link node's destination as a typed target, not a URL. Present only on a link node. */
export type AgentTreeNodeLinkV1 = internalComponents['schemas']['AgentTreeNodeLinkV1.schema'];
export type AgentExecutionApiId = internalComponents['schemas']['AgentExecutionApiId.schema'];
export type AgentAttachmentApiId = internalComponents['schemas']['AgentAttachmentApiId.schema'];
export type AgentAttachmentBatchApiId = internalComponents['schemas']['AgentAttachmentBatchApiId.schema'];

/**
 * One entry of a session's durable stream-event history (`GET .../events`): the audit-row id (a
 * stable React key), the recomposed Redis stream id (`<ms>-<seq>`) when the event reached the live
 * stream else null, and the deserialised {@link AgentStreamEventV1} payload.
 */
export type AgentExecutionEventV1 = internalComponents['schemas']['AgentExecutionEventV1.schema'];

type GeneratedAgentStreamEventV1 = messagingComponents['schemas']['AgentStreamEventV1.schema'];

export type AgentStreamEventType = GeneratedAgentStreamEventV1['type'];

/**
 * The values a run can terminate on. Each is BOTH a stream event type and a session status (the
 * terminal event type IS the resulting status), so the values satisfy the intersection — usable to
 * subscribe to the stream event AND to set the session status without a cast. Single source for the
 * two terminal Sets below.
 */
export const TerminalEventStatus = {
  Completed: 'completed',
  Failed: 'failed',
  Cancelled: 'cancelled',
} as const satisfies Record<string, AgentStreamEventType & AgentSessionStatus>;
export type TerminalEventStatus = (typeof TerminalEventStatus)[keyof typeof TerminalEventStatus];

/** Stream event types that close the stream (no further events expected). */
export const TERMINAL_STREAM_EVENTS: ReadonlySet<AgentStreamEventType> = new Set(Object.values(TerminalEventStatus));

/** Session statuses that mean a run has finished (no further events expected). */
export const TERMINAL_SESSION_STATUSES: ReadonlySet<AgentSessionStatus> = new Set(Object.values(TerminalEventStatus));

type WithId<T> = T extends unknown ? T & { id?: string } : never;

/**
 * Stream event delivered over SSE. The optional `id` is injected by AgentSessionStreamService
 * from the SSE lastEventId so the consumer can deduplicate replays on reconnect.
 */
export type AgentStreamEventV1 = WithId<GeneratedAgentStreamEventV1>;

/**
 * One numeric fact carried on a `phase_transition` — the "120 animals" behind a "Loaded animals"
 * Activity row. `key` is a server identifier looked up in the UI's copy table, never display text.
 */
export type AgentActivityMetric = messagingComponents['schemas']['AgentActivityMetric.schema'];

/**
 * The closed vocabularies the server emits on phase and subagent events. Append-only, so a value
 * this build does not recognise arrives as `unknown` rather than breaking the feed.
 */
export type AgentActivityPhase = messagingComponents['schemas']['AgentActivityPhase.schema'];
export type AgentSubagentName = messagingComponents['schemas']['AgentSubagentName.schema'];

export type AgentSessionPhase = 'idle' | 'creating' | 'streaming' | 'completed' | 'failed' | 'cancelled';

/**
 * SEND Validator result model. These types mirror the server records under
 * `com.overwatchresearch.ai.send.model` (snake_case on the wire) and are used to
 * read `execution.result` JSONB — they are not generated from OpenAPI because the
 * findings blob is stored as opaque JSONB, not a typed schema.
 */

/** Severity of a SEND validation finding. */
export type Severity = 'error' | 'warning' | 'info';

/**
 * The kind of check that produced a SEND finding. The first block are per-domain (single-file)
 * categories; the second block are cross-domain (bundle) categories emitted by the referential/
 * consistency engine. `cross_field`, `statistical_anomaly`, and `related_records` are also the
 * categories the advisory LLM pass may use.
 */
export type FindingCategory =
  | 'required_column'
  | 'column_name'
  | 'column_order'
  | 'data_type'
  | 'required_field'
  | 'iso_8601_date'
  | 'controlled_terminology'
  | 'value_list'
  | 'cross_field'
  | 'statistical_anomaly'
  | 'referential_integrity'
  | 'value_consistency'
  | 'mutual_exclusivity'
  | 'domain_mismatch'
  | 'related_records'
  | 'temporal_window'
  | 'conformance_rule';

/**
 * Why a SEND check did not complete, or why a rule does not apply to an upload. A wire value: shown
 * through the text `sendRuleOutcomes` gives it, never as-is.
 */
export type SendIncompleteReason =
  | 'binding_unresolved'
  | 'cardinality_limit_reached'
  | 'dataset_not_uploaded'
  | 'evaluation_error'
  | 'implementation_absent'
  | 'input_unavailable_in_profile'
  | 'insufficient_precision'
  | 'not_determinable'
  | 'out_of_scope_version'
  | 'required_domain_missing'
  | 'scope_unknown'
  | 'scope_use_case'
  | 'value_uninterpretable'
  | 'variable_absent'
  | 'variable_not_defined_for_domain';

/**
 * The rule a SEND finding comes from: a published rule (`cdisc`, e.g. `SEND109`) or one of the
 * validator's own checks (`benchling_in_vivo`, e.g. `IV-SEND-010`). Absent on AI notes and on results
 * stored before findings cited their rule.
 */
export interface SendRuleIdentity {
  id: string;
  publisher: 'cdisc' | 'benchling_in_vivo';
  version: string;
}

/** A single SEND validation issue. */
export interface Finding {
  severity: Severity;
  category: FindingCategory;
  column_name?: string | null;
  row_number?: number | null;
  current_value?: string | null;
  expected_value?: string | null;
  /**
   * `deterministic` for a check's own finding, `llm` for a note from Benchling In Vivo AI. A wire
   * value: shown as the branded label, never as-is.
   */
  origin?: string | null;
  message: string;
  rule?: SendRuleIdentity | null;
  /** Set when the finding is a check's notice that it could not decide something, and why. */
  disclosure?: SendIncompleteReason | null;
}

/** Aggregate counts across all findings for a validation run. */
export interface FindingsSummary {
  error_count: number;
  warning_count: number;
  info_count: number;
  submission_blocking_count: number;
}

/**
 * Exact counts for a SEND result or one of its files, taken before any limit applied, so the findings
 * shown can be read against the findings found.
 */
export interface SendReportCounts {
  failed_rules: number;
  finding_groups: number;
  affected_records: number;
  displayed_samples: number;
  omitted_records: number;
  /** Checks that said they could not decide something. Never among the failed rules. Absent on older results. */
  undecided_checks?: number;
}

/** Counts of Benchling In Vivo AI notes, kept apart from the checks' own findings. */
export interface SendAdvisoryCounts {
  observations: number;
  displayed_observations: number;
  omitted_observations: number;
}

/** A limit that left part of a SEND result out, and by how much. */
export interface SendResultTruncation {
  limit: string;
  limit_value: number;
  omitted: number;
  scope: string;
}

/** What a SEND check decided, how far it got, and why not where it did not finish. */
export interface SendCheckResult {
  verdict: 'pass' | 'fail' | 'unknown';
  evaluation_status: 'complete' | 'partial' | 'not_started' | 'error';
  reasons: Array<SendIncompleteReason>;
}

/**
 * How many rules landed in each reporting bucket, counted by the server. Read as given: the buckets'
 * predicates depend on each other, so recounting them here would get them wrong.
 */
export interface SendVerdictTally {
  passed: number;
  failed: number;
  not_applicable: number;
  not_evaluated: number;
  incomplete: number;
  total: number;
  /** Passes by a check covering only part of the rule. A subset of `passed`. */
  passed_by_proxy: number;
  /** Passes that finished, by a check answering the rule exactly. A subset of `passed`. */
  conforming: number;
}

/**
 * One rule's outcome over the whole upload. The server also sends a tier, which this UI deliberately
 * does not read: rule tiers are not assigned yet.
 */
export interface SendRuleOutcome {
  rule_id: string;
  result: SendCheckResult;
  /** Why the rule can only be checked in part, or not at all, for this kind of upload. */
  reason?: string | null;
  /** `proxy` when the check covers only part of the rule. Present only where a check ran. */
  implementation?: 'exact' | 'proxy' | null;
  evaluated_by?: 'published_encoding' | 'in_house_check' | null;
}

/** One dataset's rule tally, keyed by domain code. */
export interface SendCoverageGridRow {
  key: string;
  tally: SendVerdictTally;
}

/** The part of a SEND result's header this UI reads. */
export interface SendResultReport {
  summary: SendReportCounts;
  advisory_counts: SendAdvisoryCounts;
  truncations: Array<SendResultTruncation>;
  /** The published SEND rules' tally. Absent on older results. */
  tally?: SendVerdictTally | null;
  /** One outcome per published SEND rule. Absent on older results. */
  rule_outcomes?: Array<SendRuleOutcome> | null;
  /** One outcome per CDISC Library check outside the published rules. Absent on older results. */
  encoding_outcomes?: Array<SendRuleOutcome> | null;
  coverage_grid?: { by_domain: Array<SendCoverageGridRow> } | null;
  /** Distinct cross-domain checks that could not decide something, before any limit. Absent on older results. */
  cross_domain_undecided_checks?: number | null;
}

/**
 * Whether Benchling In Vivo AI took part in a run. Names no model, by design: the model behind it
 * can change without the customer seeing it.
 */
export interface SendRunMetadata {
  mode: 'model_assisted' | 'model_free';
  llm_used: boolean;
  llm_call_count: number;
}

/** One file's outcome within a {@link SendBundleValidationResult}. The UI paginates `findings` client-side. */
export interface SendValidationResult {
  status: string;
  domain_code: string;
  row_count: number;
  summary: FindingsSummary;
  /** This file's exact counts. Absent only on results stored before bounded delivery. */
  counts?: SendReportCounts | null;
  /** This file's Benchling In Vivo AI note counts. Absent on the same older results. */
  advisory?: SendAdvisoryCounts | null;
  findings: Array<Finding>;
  truncations?: Array<SendResultTruncation> | null;
}

/**
 * A single cross-domain finding: an issue that spans two or more domains in the bundle (e.g. a
 * USUBJID in LB that DM never defines). `domains` is the sorted set of domain codes the check
 * spanned and is what the server groups on. `origin` distinguishes a deterministic Java verdict
 * from an advisory LLM observation (which can never be an `error`). Collapsed findings carry an
 * `occurrence_count` and a bounded `sample_values` list.
 */
export interface CrossDomainFinding {
  severity: Severity;
  category: FindingCategory;
  domains: Array<string>;
  key_variable?: string | null;
  current_value?: string | null;
  expected_value?: string | null;
  occurrence_count?: number | null;
  sample_values?: Array<string> | null;
  origin?: string | null;
  message: string;
  rule?: SendRuleIdentity | null;
  /** Set when the finding is a check's notice that it could not decide something, and why. */
  disclosure?: SendIncompleteReason | null;
}

/** Cross-domain findings grouped by the sorted set of domain codes the checks spanned. */
export interface CrossDomainFindingGroup {
  domains: Array<string>;
  findings: Array<CrossDomainFinding>;
}

/**
 * The full structured outcome of a bundle validation run, persisted to
 * `agent_executions.result`. `per_domain` carries one {@link SendValidationResult} per file (the
 * single-domain tier), while `cross_domain` carries the referential/consistency findings grouped by
 * domain-set.
 */
export interface SendBundleValidationResult {
  status: string;
  per_domain: Array<SendValidationResult>;
  cross_domain: Array<CrossDomainFindingGroup>;
  summary: FindingsSummary;
  /** The whole result's exact counts and every limit that applied. Absent on older results. */
  report?: SendResultReport | null;
  /** Whether Benchling In Vivo AI took part. Absent on older results. */
  run_metadata?: SendRunMetadata | null;
}

/** Aliases of the schemas the server publishes and tests against its records. Change the server schema, not these. */
export type SendInputFailureKind = internalComponents['schemas']['SendInputFailureKind.schema'];
export type SendInputFailure = internalComponents['schemas']['SendInputFailure.schema'];
export type SendInputFailureResult = internalComponents['schemas']['SendInputFailureResult.schema'];

export interface StreamingText {
  executionApiId: AgentExecutionApiId;
  accumulatedText: string;
  isStreaming: boolean;
}
